import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePortal, RESERVED_SLUGS, SLUG_PATTERN, type ClientDetail, type CreateClientBody } from "@app/portal-sdk";
import { Alert, Box, Button, PageHeader, SectionCard, Stack, TextField, Typography } from "@app/ui-kit";
import { slugify } from "../lib/format";
import { errorMessage } from "../lib/useApiQuery";

/**
 * The "portal address" is the last part of the client's URL:
 * https://portal.blairkoch.com/c/<address>. It is stored as `slug` in the API.
 */
const addressProblem = (address: string): string | null => {
  if (!address) return "A portal address is required";
  if (!SLUG_PATTERN.test(address)) return "Use 3–40 lowercase letters, digits or hyphens";
  if ((RESERVED_SLUGS as readonly string[]).includes(address)) return "That address is reserved by the portal";
  return null;
};

export const NewClient = () => {
  const navigate = useNavigate();
  const { api } = usePortal();
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [addressTouched, setAddressTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const effectiveAddress = addressTouched ? address : slugify(name);
  const problem = addressProblem(effectiveAddress);
  const portalUrl = `${window.location.origin}/c/${effectiveAddress || "…"}`;

  const submit = async () => {
    if (problem || name.trim().length < 2) return;
    setSaving(true);
    setError(null);
    try {
      const body: CreateClientBody = { name: name.trim(), slug: effectiveAddress };
      const created = await api.request<ClientDetail>("/admin/clients", { method: "POST", json: body });
      navigate(`/admin/clients/${created.id}`, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setSaving(false);
    }
  };

  return (
    <Stack spacing={5}>
      <PageHeader
        eyebrow="Admin Console"
        title="New client"
        subtitle="Creates the client's portal. You can invite people and enable applications right after."
      />
      <SectionCard eyebrow="Client" title="Details">
        <Stack spacing={3} sx={{ maxWidth: 560 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Client name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Acme Holdings"
            autoFocus
            fullWidth
          />
          <Box>
            <TextField
              label="Portal address"
              value={effectiveAddress}
              onChange={(event) => {
                setAddressTouched(true);
                setAddress(event.target.value.toLowerCase());
              }}
              helperText={problem ?? "Suggested from the client name. Lowercase letters, digits and hyphens."}
              error={Boolean(problem) && (addressTouched || name.length > 0)}
              fullWidth
            />
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mt: 1 }}>
              The client will open their portal at{" "}
              <Box component="span" sx={{ fontFamily: "monospace", color: "text.primary" }}>
                {portalUrl}
              </Box>
              . This cannot be changed later without breaking their links.
            </Typography>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button variant="contained" onClick={() => void submit()} disabled={saving || Boolean(problem) || name.trim().length < 2}>
              Create client
            </Button>
            <Button variant="text" onClick={() => navigate("/admin")} disabled={saving}>
              Cancel
            </Button>
          </Stack>
        </Stack>
      </SectionCard>
    </Stack>
  );
};
